import { WORKSPACE_FARBEN, WORKSPACE_FARB_LABELS, type WorkspaceFarbe } from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { useRouterState } from '@tanstack/react-router'
import { cn } from '../lib/cn'
import { NAVIGATION } from '../lib/navigation'
import { workspacesQuery } from '../lib/queries'
import { useAktiveWelt } from '../lib/welt'

/**
 * **Welche Klassen zu welcher Farbe gehören** – ausgeschrieben, nicht zusammengesetzt.
 *
 * `` `bg-ws-${farbe}-bg` `` wäre kürzer und funktionierte nicht: Tailwind liest den Quelltext
 * und findet nur Klassennamen, die dort **buchstäblich** stehen. Eine zusammengesetzte Klasse
 * landet nicht im Stylesheet, und die Marke erschiene farblos – auf dem Rechner beim Entwickeln
 * womöglich noch richtig, weil eine andere Datei dieselbe Klasse zufällig enthält.
 *
 * Die Werte selbst stehen in `theme.css`; hier steht nur, welcher Name welchen nimmt.
 */
const FARBKLASSEN: Record<WorkspaceFarbe, { flaeche: string; text: string; punkt: string }> = {
  grau: { flaeche: 'bg-ws-grau-bg', text: 'text-ws-grau', punkt: 'bg-ws-grau' },
  blau: { flaeche: 'bg-ws-blau-bg', text: 'text-ws-blau', punkt: 'bg-ws-blau' },
  gruen: { flaeche: 'bg-ws-gruen-bg', text: 'text-ws-gruen', punkt: 'bg-ws-gruen' },
  orange: { flaeche: 'bg-ws-orange-bg', text: 'text-ws-orange', punkt: 'bg-ws-orange' },
  violett: { flaeche: 'bg-ws-violett-bg', text: 'text-ws-violett', punkt: 'bg-ws-violett' },
  rot: { flaeche: 'bg-ws-rot-bg', text: 'text-ws-rot', punkt: 'bg-ws-rot' },
}

/** Ein unbekannter Name aus der Datenbank soll nichts kaputtmachen, sondern grau sein. */
export function farbklassen(farbe: string | null | undefined) {
  return FARBKLASSEN[
    (WORKSPACE_FARBEN as readonly string[]).includes(farbe ?? '')
      ? (farbe as WorkspaceFarbe)
      : 'grau'
  ]
}

/**
 * **Ein farbiger Punkt** – für Listen, in denen kein Platz für die ganze Marke ist.
 */
export function WorkspacePunkt({
  farbe,
  className,
}: {
  farbe?: string | null
  className?: string
}) {
  return (
    <i className={cn('size-[7px] shrink-0 rounded-full', farbklassen(farbe).punkt, className)} />
  )
}

/**
 * **Die Marke der Arbeitswelt** – Symbol, Name, Farbe.
 *
 * Sie ist bewusst kein Knopf: umgeschaltet wird im Menü, und zwei Stellen mit derselben Wirkung
 * sind zwei Stellen, an denen man sich fragt, ob sie dasselbe tun. Hier steht eine **Auskunft**.
 */
export function WorkspaceMarke({
  label,
  icon,
  farbe,
  gross,
  className,
  titel,
}: {
  label: string
  icon?: string | null
  farbe?: string | null
  /** Größer – die Ausführung für die Kopfzeile. */
  gross?: boolean
  className?: string
  titel?: string
}) {
  const f = farbklassen(farbe)
  return (
    <span
      title={titel}
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-full font-semibold',
        f.flaeche,
        f.text,
        gross ? 'px-2.5 py-1 text-[12.5px]' : 'px-2 py-0.5 text-[11px]',
        className,
      )}
    >
      <span className="leading-none">{icon || '📁'}</span>
      <span className="truncate">{label}</span>
    </span>
  )
}

/**
 * **Die Arbeitswelt einer einzelnen Aufgabe** – für „Mein Tag" (Z-07.8).
 *
 * ## Warum sie hier gebraucht wird und im Kopf nicht reicht
 *
 * „Mein Tag" ist die eine Ansicht, die über **alle** Arbeitswelten geht (E-234) – ein Mensch hat
 * einen Tag, auch wenn seine Arbeit aus drei Welten kommt. Genau deshalb steht dort **keine**
 * Marke im Kopf: es gibt keine aktive. Die Zeilen mischen sich dann aber, und „Überweisung
 * machen" steht neben „IOR 2.0 – Schnittstelle" ohne jeden Hinweis, dass das zwei Welten sind.
 *
 * ## Warum sie bei einer einzigen Welt verschwindet
 *
 * Dieselbe Überlegung wie beim Umschalter: eine Marke, die an jeder Zeile dasselbe sagt, ist
 * Rauschen. Zwei Riegel dagegen:
 *
 * 1. **hier**: unter zwei Arbeitswelten gibt es nichts zu unterscheiden;
 * 2. **beim Aufrufer**: eine Liste, die nur Aufgaben aus einer Welt zeigt, braucht sie auch dann
 *    nicht, wenn es daneben noch zwei andere gibt – dafür ist `zeigen` da, und die Frage
 *    beantwortet {@link weltenGemischt}.
 */
export function AufgabenWeltMarke({
  workspaceId,
  zeigen = true,
  className,
}: {
  workspaceId?: string | null
  zeigen?: boolean
  className?: string
}) {
  const { data: workspaces } = useQuery(workspacesQuery)
  if (!zeigen || !workspaceId || (workspaces?.length ?? 0) < 2) return null

  const welt = workspaces?.find((w) => w.id === workspaceId)
  if (!welt) return null

  return (
    <WorkspaceMarke
      label={welt.label}
      icon={welt.icon}
      farbe={welt.farbe}
      className={className ?? ''}
      titel={`Diese Aufgabe liegt in „${welt.label}“`}
    />
  )
}

/**
 * **Kommen diese Aufgaben aus mehr als einer Arbeitswelt?**
 *
 * Der Prüfstein für die Marke: sie soll erscheinen, wenn sie **unterscheidet**, und schweigen,
 * wenn sie nur wiederholt. Eine Liste aus lauter „Entwicklung" braucht sie nicht, auch wenn es
 * daneben noch zwei andere Welten gibt.
 */
export function weltenGemischt(aufgaben: readonly { workspaceId?: string | null }[]): boolean {
  const gesehen = new Set<string>()
  for (const a of aufgaben) {
    if (a.workspaceId) gesehen.add(a.workspaceId)
    if (gesehen.size > 1) return true
  }
  return false
}

/**
 * **Die Farbwahl** – sechs Punkte, keine Eingabe.
 *
 * Steht überall gleich: beim Anlegen in der Seitenleiste und in der Verwaltung. Ein Farbwähler
 * wäre freier und falsch – die Begründung steht bei `WORKSPACE_FARBEN` im geteilten Paket.
 */
export function FarbWahl({
  wert,
  onWahl,
  className,
}: {
  wert: WorkspaceFarbe
  onWahl: (farbe: WorkspaceFarbe) => void
  className?: string
}) {
  return (
    <div className={cn('flex flex-wrap gap-1.5', className)}>
      {WORKSPACE_FARBEN.map((f) => (
        <button
          key={f}
          type="button"
          onClick={() => onWahl(f)}
          aria-label={WORKSPACE_FARB_LABELS[f]}
          aria-pressed={wert === f}
          title={WORKSPACE_FARB_LABELS[f]}
          className={cn(
            'size-[18px] rounded-full border-2',
            FARBKLASSEN[f].punkt,
            wert === f ? 'border-ink' : 'border-transparent hover:border-line2',
          )}
        />
      ))}
    </div>
  )
}

/**
 * **Die aktive Arbeitswelt im Kopf jeder gebundenen Seite** (Nils, 26.08.2026).
 *
 * > *„Bei den Seiten wo die Trennung greift, da im Header zentral auch das aktive Workspace
 * > immer anzeigen (Leitstand, Pools, Alle Aufgaben etc.), dass man das irgendwie gut sieht."*
 *
 * ## Warum sie sich selbst entscheidet, ob sie erscheint
 *
 * Weil die Alternative wäre, in **sechs** Ansichten eine Zeile einzubauen – und in der siebten,
 * die nächste Woche dazukommt, sie zu vergessen. Genau diesen Fehler hat Z-07 schon zweimal
 * gemacht: erst am Server, dann im Client, beide Male gefunden von Nils und nicht von mir. Die
 * Antwort war beide Male dieselbe – **eine** Stelle, die nicht übersehen werden kann.
 *
 * Hier ist es die Kopfzeile: sie steht in jeder Ansicht, und `NAVIGATION` weiß bereits, welcher
 * Punkt an eine Arbeitswelt gebunden ist (`workspaceGebunden`, Pflichtfeld). Wer eine neue
 * gebundene Ansicht einträgt, bekommt die Marke damit umsonst.
 *
 * ## Wann sie schweigt
 *
 * - auf ungebundenen Seiten („Mein Tag", „Eingang", Einstellungen) – dort wäre sie **falsch**:
 *   diese Ansichten gehen über alle Arbeitswelten (E-234);
 * - solange es nur eine Arbeitswelt gibt. Eine Marke, die immer dasselbe sagt, liest niemand;
 *   und direkt nach der Migration soll die Anwendung aussehen wie vorher.
 */
export function AktiveWeltMarke() {
  const pfad = useRouterState({ select: (s) => s.location.pathname })
  const { welt } = useAktiveWelt()
  const { data: workspaces } = useQuery(workspacesQuery)

  const eintrag = NAVIGATION.find((e) => e.path === pfad)
  if (!eintrag?.workspaceGebunden) return null
  if (!welt || (workspaces?.length ?? 0) < 2) return null

  return (
    <WorkspaceMarke
      gross
      label={welt.label}
      icon={welt.icon}
      farbe={welt.farbe}
      className="hidden sm:inline-flex"
      titel={`Diese Ansicht zeigt nur „${welt.label}“`}
    />
  )
}
